import { useState, useEffect } from 'react';

export interface ConsentPreferences {
  analytics: boolean;
  ads: boolean;
  personalization: boolean;
  necessary: boolean;
}

const CONSENT_COOKIE_NAME = 'triple_freezer_consent';
const CONSENT_COOKIE_EXPIRY_DAYS = 365; // 1 year

export const useConsent = () => {
  const [consent, setConsent] = useState<ConsentPreferences | null>(null);
  const [isConsentBannerVisible, setIsConsentBannerVisible] = useState(false);

  // Load consent from cookie on mount
  useEffect(() => {
    const savedConsent = getConsentFromCookie();
    console.log('[Consent] Loading consent from cookie:', savedConsent);
    if (savedConsent) {
      setConsent(savedConsent);
      updateGoogleConsent(savedConsent);
    } else {
      console.log('[Consent] No saved consent found, showing banner');
      setIsConsentBannerVisible(true);
    }
  }, []);

  // Get consent from cookie
  const getConsentFromCookie = (): ConsentPreferences | null => {
    try {
      const cookie = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));
      
      if (cookie) {
        const value = cookie.split('=')[1];
        return JSON.parse(decodeURIComponent(value));
      }
    } catch (error) {
      console.error('Error parsing consent cookie:', error);
    }
    return null;
  };

  // Save consent to cookie
  const saveConsentToCookie = (preferences: ConsentPreferences) => {
    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + CONSENT_COOKIE_EXPIRY_DAYS);
      
      const cookieValue = encodeURIComponent(JSON.stringify(preferences));
      document.cookie = `${CONSENT_COOKIE_NAME}=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    } catch (error) {
      console.error('Error saving consent cookie:', error);
    }
  };

  // Update Google consent signals
  const updateGoogleConsent = (preferences: ConsentPreferences) => {
    try {
      // Check if gtag is available
      if (typeof window !== 'undefined' && (window as any).gtag) {
        const gtag = (window as any).gtag;
        
        gtag('consent', 'update', {
          'analytics_storage': preferences.analytics ? 'granted' : 'denied',
          'ad_storage': preferences.ads ? 'granted' : 'denied',
          'ad_user_data': preferences.ads ? 'granted' : 'denied',
          'ad_personalization': preferences.personalization ? 'granted' : 'denied',
        });

        console.log('[Consent] Updated Google consent signals:', preferences);
      }
    } catch (error) {
      console.error('Error updating Google consent:', error);
    }
  };

  // Handle consent change
  const handleConsentChange = (preferences: ConsentPreferences) => {
    setConsent(preferences);
    saveConsentToCookie(preferences);
    updateGoogleConsent(preferences);
    setIsConsentBannerVisible(false);
    
    console.log('[Consent] Consent preferences updated:', preferences);
  };

  // Reset consent (for testing or user request)
  const resetConsent = () => {
    // Remove cookie
    document.cookie = `${CONSENT_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    setConsent(null);
    setIsConsentBannerVisible(true);
    
    // Reset Google consent to default
    if (typeof window !== 'undefined' && (window as any).gtag) {
      const gtag = (window as any).gtag;
      gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
      });
    }
  };

  // Check if specific consent is granted
  const hasConsent = (type: keyof ConsentPreferences): boolean => {
    return consent?.[type] ?? false;
  };

  return {
    consent,
    isConsentBannerVisible,
    handleConsentChange,
    resetConsent,
    hasConsent,
  };
}; 